import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useStageRepullPartners } from "@/features/settings/use-stage-repull-partners";

// SPA port of /app/admin/settings/integrations/_components/RePullPartnersDialog.tsx.
// Today the click stages a job marker; vendor ops runs the actual script
// from the deploy console. Session 10 will move it to an Edge Function.

export function RePullPartnersDialog({
  partnerCount,
}: {
  partnerCount: number;
}) {
  const [open, setOpen] = useState(false);
  const mutation = useStageRepullPartners();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <RefreshCw className="mr-1.5 h-3 w-3" />
          Re-pull now
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Re-pull partners from HubSpot
          </DialogTitle>
          <DialogDescription>
            This re-reads {partnerCount.toLocaleString("en-IE")} partner records
            from HubSpot and overwrites the local snapshot. Vendor staff may see
            in-progress edits get clobbered. Run during low-traffic windows.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
          <p>
            Today this click stages a job marker; the actual re-pull is run from
            the deploy console:
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-background px-2 py-1.5 font-mono text-[11px]">
            pnpm tsx scripts/backfill-partners-from-hubspot.ts
          </pre>
          <p className="mt-2">
            Session 10 will move this to an Edge Function with a polling status
            read.
          </p>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(undefined, {
                onSuccess: () => {
                  toast.success("Re-pull job staged");
                  setOpen(false);
                },
              })
            }
          >
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3 w-3" />
            )}
            Stage re-pull
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
